import { simpleAxios } from "@/utils/http";
import type { AxiosResponse } from "axios";
import { defineStore, acceptHMRUpdate } from "pinia"
import { useEventsStore } from "@/stores/apps/events";
import { useContentStore } from "@/stores/apps/content";
import { getTranslatedObject } from "@/utils/translate";
import { useMapStore } from "@/stores/map";
import i18n from "@/utils/i18n";
import { updateRankingDisplayMode } from "@/composables/useRankingDisplay";
import type { RankingDisplayMode } from "@/config/rankingDisplayModes";
import type { CluedoAIData } from "@/stores/apps/interfaces/cluedo_ai_interfaces";
import { parseMapCoords } from "@/utils/mapCenter";

const { t } = i18n.global

export const useCluedoAIStore = defineStore("cluedo_ai", {
	state: () => ({
        cluedoAIData: {} as CluedoAIData, 
        ws: null,
        allRecords: [],
		teams: [],
		current_cluedo_ai_team: {},
		current_cluedo_ai_object: {},
		current_cluedo_ai_character: {},

		// Game Settings
		primaryColor:"",
		secondaryColor:"",
		timerColor:"",
		backgroundUrl:"",
		pointsIcon: "",
	}),
	actions: {
		async fetchCluedoAIData (api_key:string)
		{
			await simpleAxios("get", "events/event_full/" + api_key, {})
			.then((response: AxiosResponse) => {
                console.log(response.data);
                
                this.cluedoAIData = response.data

				this.primaryColor = response.data.game.primary_color;
				this.secondaryColor = response.data.game.secondary_color;
				this.timerColor = response.data.game.timer_color;
				this.backgroundUrl = response.data.game.background;
				this.pointsIcon = decodeURIComponent(response.data.game.points_icon);
			})
			.catch((e: any) => {
				console.log(e);
			});
		},
		async fetchAllRecords(api_key:string)
		{
			await simpleAxios("get", "cluedoAi/get_record_conversation/" + api_key, {})
			.then((response: AxiosResponse) => {
                console.log(response.data);
                this.allRecords = response.data
			})
		},
		setObject(object: any) {
			
			this.current_cluedo_ai_object = object
		},
		setCharacter(character: any) {
			this.current_cluedo_ai_character = character
			if(!('character' in this.current_cluedo_ai_character))
			{
				this.current_cluedo_ai_character.character = this.current_cluedo_ai_character.id
			}
		},
		updateCharacterConversation(record: any) {
			if(this.allRecords.find(e => e.id == record.id))
			{
				this.allRecords[this.allRecords.findIndex(e => e.id == record.id)] = record
			}
			else
			{
				this.allRecords.push(record)
			}
			if(record.team.id == this.current_cluedo_ai_team.id)
			{
				this.current_cluedo_ai_character = record
			}
		},
		setColorStyle(settings:any)
		{
			console.log(settings);
			
			this.primaryColor = settings.primary_color;
			this.secondaryColor = settings.secondary_color;
			this.timerColor = settings.timer_color;
			this.backgroundUrl = settings.background;
			this.pointsIcon = settings.points_icon;
		},
		async setRankingDisplayMode(event_api_key:string, mode: RankingDisplayMode)
		{
			await updateRankingDisplayMode(event_api_key, mode)
			this.cluedoAIData.ranking_display_mode = mode
		},
		addDataMarkers()
		{			
			const mapStore = useMapStore()
			const initCoords = parseMapCoords(this.cluedoAIData.latitude, this.cluedoAIData.longitude)
			const finishCoords = parseMapCoords(this.cluedoAIData.finish_latitude, this.cluedoAIData.finish_longitude)
			if(initCoords && !('initMarker' in mapStore.markers))
				{
					mapStore.addMarker({
						id: 'initMarker',
						latitude: initCoords.lat,
						longitude: initCoords.lng,
						title: t('gm.dashboards.init'),
						radius: 0,
						isDraggable: false,
						customClass: {type: 'flag', color: 'green'}
					})
				}
				if(finishCoords && !('finishMarker' in mapStore.markers))
				{
					mapStore.addMarker({
						id: 'finishMarker',
						latitude: finishCoords.lat,
						longitude: finishCoords.lng,
						title: t('gm.dashboards.finish'),
						radius: 0,
						isDraggable: false,
						customClass: {type: 'flag', color: 'red'}
					})
				}
		},
		addFinishMarker()
		{
			const mapStore = useMapStore()
			const finishCoords = parseMapCoords(this.cluedoAIData.finish_latitude, this.cluedoAIData.finish_longitude)
			if(finishCoords && !('finishMarker' in mapStore.markers))
			{
				mapStore.addMarker({
					id: 'finishMarker',
					latitude: finishCoords.lat,
					longitude: finishCoords.lng,
					title: t('gm.dashboards.finish'),
					radius: 0,
					isDraggable: false,
					customClass: {type: 'flag', color: 'red'}
				})
			}
		}
	},

	persist: {
		// paths: ["events"]
	},
	getters:{
        getRankingGM() {
			const ranking: any[] = []
			
			const teams = this.teams.sort((a, b) => b.team.points - a.team.points)

			teams.forEach(t => {
					const objects_act = this.getActInformation().objects
					const characters_act = this.getActInformation().characters
					const questions_act = this.getActInformation().questions
					
					console.log(t, questions_act);
					const unlock_objects_current_act = objects_act.filter(e => e.act == this.getCurrentActID() && t.unlock_objects.includes(e.id))
					const objects_found = unlock_objects_current_act.length + " / " + objects_act.length
					const characters_team = [...new Set(this.allRecords.filter(e => e.team.id == t.id && characters_act.find(c => c.id == e.character)))]
					const characters_found = characters_team.length + " / " + characters_act.length
					const questions_answered = Object.keys(t.questions || {})
						.filter(key => questions_act.some(q => q.id.toString() == key && t.questions[key] != "")).length + 
						" / " + questions_act.length
					

					ranking.push({
						id: t.team.id,
						name: t.team.name,
						full_name: t.team.name_id + ". " + t.team.name,
						position: ranking.length + 1,
						points: t.team.points,
						objects_found: objects_found,
						characters_found: characters_found,
						questions_answered: questions_answered,
						characters: characters_team,
						objects: t.unlock_objects,
						questions: t.questions,
					})
			})
			return ranking


        },
		
		getCharacterKeyInformationTeam: (state) => (character_id: any, team_id: any) => {
			const contentStore = useContentStore()
			const characters_content = [...contentStore.content.cluedo_ai_character];
			const character_content = characters_content.find(e => e.id == character_id)
			console.log(state.allRecords.find(c => c.character == character_id && c.team.id == team_id));
			if(state.allRecords.find(c => c.character == character_id && c.team.id == team_id))
			{
				return {
					count_key_information_unlocked: state.allRecords.find(c => c.character == character_id).unlock_key_information ? state.allRecords.find(c => c.character == character_id).unlock_key_information.length : 0,
					count_key_information_total: getTranslatedObject(character_content, 'key_information').length
				}

			}
			else

			{
				return {
					count_key_information_unlocked: null,
					count_key_information_total: getTranslatedObject(character_content, 'key_information').length
				}
			}
		},



        getTeamRanking: state => (): any | undefined => {
            let ranking = []

			ranking = state.teams.sort((a, b) => b.team.points - a.team.points)
			
			let pos = 1
			let beforePoints = -1
			ranking.forEach(r => {
				if(beforePoints != -1 && beforePoints != r.team.points)
				{
					pos++
				}
				beforePoints = r.team.points
				r.position = pos
				r.points = r.team.points
				r.name = r.team.name
				
			});
			console.log(ranking);
			return ranking
        },
		getCurrentActID: state => ()=>{
			if(state.cluedoAIData.current_act === undefined)
			{
				return null
			}
			const contentStore = useContentStore()
			const act_info = contentStore.content.cluedo_ai_act.find((act:any) => act.id == state.cluedoAIData.game.order[state.cluedoAIData.current_act])
			return act_info.id
		},
		getActInformation: state => (): any | undefined => {
			const contentStore = useContentStore()
			
			if (!('cluedo_ai_act' in contentStore.content)) {
				return {};
			}

			const act = contentStore.content.cluedo_ai_act.find((act2:any) => act2.id == state.cluedoAIData.game.order[state.cluedoAIData.current_act])
			
			// Create a new object instead of modifying the existing one
			return {
				act: {...act},
				characters: contentStore.content.cluedo_ai_character.filter(e => e.act == act.id),
				objects: contentStore.content.cluedo_ai_object.filter(e => e.act == act.id),
				questions: contentStore.content.cluedo_ai_questions.filter(e => e.act == act.id)
			};
		},
		getActInformationId: state => (id_act: any): any | undefined => {
			const contentStore = useContentStore()
			if (!('cluedo_ai_act' in contentStore.content)) {
				return {};
			}

			const act = contentStore.content.cluedo_ai_act.find((act2:any) => act2.id == id_act)
			// Create a new object instead of modifying the existing one
			return {
				act: {...act},
				characters: contentStore.content.cluedo_ai_character.filter(e => e.act == act.id),
				objects: contentStore.content.cluedo_ai_object.filter(e => e.act == act.id),
				questions: contentStore.content.cluedo_ai_questions.filter(e => e.act == act.id)
			};
		},
		getTeamCharactersConversations: (state) => () => {
			const contentStore = useContentStore()
			const charactersAct = contentStore.content.cluedo_ai_character.filter(e => e.act == state.cluedoAIData.game.order[state.cluedoAIData.current_act])
			const characters = []
			
			charactersAct.forEach(e => {
				let character = state.allRecords.find(c => c.character == e.id && c.team.id == state.current_cluedo_ai_team.id)
				if(character)

				{
					character.content = e
					character.found = true
				}
				else
				{
					character = {}
					character.content = e
					character.found = false
				}
				
				characters.push(character)
			})
			
			return characters
		},


		getCharacterConversation: (state) => (character: any) => {
			if(state.current_cluedo_ai_team.id == undefined || state.current_cluedo_ai_character.id == undefined)
			{
				return {}
			}
			return state.allRecords.find(e => e.character == state.current_cluedo_ai_character.id && e.team == state.current_cluedo_ai_team.id)
		},
		getSecondsTime: (state) =>{
			if(state.cluedoAIData.timer_finish == undefined)
			{
				return;
			}
			const now:Date = new Date();			
			return new Date(state.cluedoAIData.timer_finish);
		},
		getKeyInformationCount: (state) => {
			const character = state.current_cluedo_ai_character.character;
			const contentStore = useContentStore();
			if(!('cluedo_ai_character' in contentStore.content))
			{
				return 0
			}
			const currentCharacter = contentStore.content.cluedo_ai_character.find(e => e.id == character);
			if(currentCharacter == undefined)
			{
				return 0
			}
			
			return Object.keys(getTranslatedObject(currentCharacter, 'key_information')).length;
		},
		getKeyInformationUnlocked: (state) => {
			return function(this: any) {
				const unlocked: any[] = []
				
				if(!('unlock_key_information' in state.current_cluedo_ai_character) || 
					!state.current_cluedo_ai_character.unlock_key_information?.length)
				{
					return unlocked
				}
				
				state.current_cluedo_ai_character.unlock_key_information.forEach(key => {
					const character = this.getCurrentCharacter()
					const key_info = getTranslatedObject(character, 'key_information').find(e => e.id == key)
					
					if(key_info != undefined) {
						unlocked.push(key_info)
					}
				})
				
				return unlocked
			}
		},
		getCurrentCharacter: (state) => () => {
			const contentStore = useContentStore()
			if(state.current_cluedo_ai_character == undefined)
			{
				return {}
			}
			
			const character = contentStore.content.cluedo_ai_character.find(e => e.id == state.current_cluedo_ai_character.character)
			
			return character
		},
		getCharacterKeyInformation: (state) => (character_id: any) => {
			const contentStore = useContentStore()
			const character_info = contentStore.content.cluedo_ai_character.find(e => e.id == character_id)
			return character_info
		},
		getTeamQuestions: (state) => (team_id: any, question_id: any) => {
			const team = state.teams.find(e => e.team.id == team_id)
			console.log(team);
			if(team == undefined)
			{
				return []
			}
			return team.questions[question_id]
		},
		getFeedbackQuestion: (state) => (question_id: any) => {
			const team = state.teams.find(e => e.id == state.current_cluedo_ai_team.id)
			if(team && 'feedback_questions' in team) {
				return team.feedback_questions.find(e => e.question_id == question_id)
			} else {
				return {}
			}
		},
		getTeamQuestionsSolved: (state) => (team_id: any, question_id: any) => {
			const team = state.teams.find(e => e.team.id == team_id)
			console.log(team);
			if(team == undefined)
			{
				return []
			}
			return team.feedback_questions.find(e=> e.question_id == question_id)
		}
	}
})

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useCluedoAIStore, import.meta.hot))
}
